import { Address, CheckoutSelectors, Country, Customer, FormField, CustomerAddress } from '@bigcommerce/checkout-sdk'
import { withFormik, FormikProps } from 'formik'
import React, { createRef, PureComponent, ReactNode, RefObject } from 'react'
import { lazy, object } from 'yup'
import { connect } from 'react-redux'

import B3Checkout from '../b3checkout'
import { getAddressFormFieldsValidationSchema, getTranslateAddressError, isValidCustomerAddress, mapAddressToFormValues, AddressForm, AddressFormValues, AddressSelect } from '../address'
import { getCustomFormFieldsValidationSchema } from '../formFields'
import { withLanguage, TranslatedString, WithLanguageProps } from '../locale'
import { OrderComments } from '../orderComments'
import { Button, ButtonVariant } from '../ui/button'
import { Fieldset, Form } from '../ui/form'
import { LoadingOverlay } from '../ui/loading'

import StaticBillingAddress from './StaticBillingAddress'
import CheckoutStepType from '../checkout/CheckoutStepType';

export type BillingFormValues = AddressFormValues & { orderComment: string } & { b2bCustomFields?: any }

export interface BillingFormProps {
    billingAddress?: Address
    countries: Country[]
    countriesWithAutocomplete: string[]
    customer: Customer
    customerMessage: string
    googleMapsApiKey: string
    isUpdating: boolean
    methodId?: string
    shouldShowOrderComments: boolean
    getFields(countryCode?: string): FormField[]
    onSubmit(values: BillingFormValues): void
    onUnhandledError(error: Error): void
    updateAddress(address: Partial<Address>): Promise<CheckoutSelectors>
    type: CheckoutStepType
}

interface B3Props {
    b3Company: any
    isB3User: boolean
    checkoutSettings: any
}
interface BillingFormState {
    isResettingAddress: boolean
}

class BillingForm extends PureComponent<B3Props & BillingFormProps & WithLanguageProps & FormikProps<BillingFormValues>, BillingFormState> {
    state: BillingFormState = {
        isResettingAddress: false,
    };

    private addressFormRef: RefObject<HTMLFieldSetElement> = createRef();

    get shouldShowSaveAddress() {
        const {
            customer: { isGuest },
            isB3User,
        } = this.props
        const {
            storage,
            constants,
        } = B3Checkout.utils

        return isB3User
            ? (storage.B3AddressBookActionPermission.getValue() === constants.B3AddressBookPermission.isAllow)
            : !isGuest
    }

    get shouldShowSaveAddressInCheckoutSettings() {
        const {
            checkoutSettings,
        } = this.props

        return checkoutSettings['billing.address.save']
    }


    get addresses(): CustomerAddress[] {
        const {
            customer,
            isB3User,
            b3Company,
        } = this.props

        return isB3User
            ? b3Company.addresses.filter((address: { isBilling: number }) => address.isBilling === 1)
            : customer.addresses
    }

    get shouldShowEnterNewAddress() {
        const {
            checkoutSettings,
        } = this.props

        const hasValidCustomerAddress = this.validateAddress()
        return (
            hasValidCustomerAddress
            && checkoutSettings['billing.address.add']
        )
    }

    validateAddress() {
        const {
            billingAddress,
            getFields,
            isB3User
        } = this.props

        const formFields = getFields(billingAddress?.countryCode)

        if (!isB3User) return isValidCustomerAddress(billingAddress, this.addresses, formFields)

        const isValidateExtrasField = B3Checkout.extrasFiles.isValidExtrasFiles('billing')

        return isValidateExtrasField && B3Checkout.utils.util.validateAddress(billingAddress, this.addresses, formFields)
    }

    render(): ReactNode {
        const {
            googleMapsApiKey,
            billingAddress,
            countriesWithAutocomplete,
            getFields,
            countries,
            isUpdating,
            setFieldValue,
            shouldShowOrderComments,
            values,
            methodId,
            type,
        } = this.props

        const shouldRenderStaticAddress = methodId === 'amazonpay'
        const allFormFields = getFields(values.countryCode)
        const customFormFields = allFormFields.filter(({ custom }) => custom)
        const hasCustomFormFields = customFormFields.length > 0
        const editableFormFields = shouldRenderStaticAddress && hasCustomFormFields ? customFormFields : allFormFields
        const { isResettingAddress } = this.state

        const hasAddresses = this.addresses && this.addresses.length > 0
        const hasValidCustomerAddress = this.validateAddress()


        return (
            <Form autoComplete="on">
                {shouldRenderStaticAddress && billingAddress &&
                    <div className={'form-fieldset'}>
                        <StaticBillingAddress address={billingAddress} />
                    </div>}

                <Fieldset id="checkoutBillingAddress" ref={this.addressFormRef}>
                    {hasAddresses && !shouldRenderStaticAddress &&
                        <Fieldset id="billingAddresses">
                            <LoadingOverlay isLoading={isResettingAddress}>
                                <AddressSelect
                                    addresses={this.addresses}
                                    onSelectAddress={this.handleSelectAddress}
                                    onUseNewAddress={this.handleUseNewAddress}
                                    selectedAddress={hasValidCustomerAddress ? billingAddress : undefined}
                                />
                            </LoadingOverlay>
                        </Fieldset>}

                    {
                        this.shouldShowEnterNewAddress && (
                            <p>
                                OR <a onClick={this.handleUseNewAddress}>{B3Checkout.locales.tips['b3.addresses.enterNew']}</a>
                            </p>
                        )
                    }

                    {!hasValidCustomerAddress &&
                        <LoadingOverlay isLoading={isResettingAddress}>
                            <AddressForm
                                countries={countries}
                                countriesWithAutocomplete={countriesWithAutocomplete}
                                countryCode={values.countryCode}
                                formFields={editableFormFields}
                                googleMapsApiKey={googleMapsApiKey}
                                setFieldValue={setFieldValue}
                                shouldShowSaveAddress={this.shouldShowSaveAddress && this.shouldShowSaveAddressInCheckoutSettings}
                                isLoadingB2bAddress={true}
                                type={type}
                            />
                        </LoadingOverlay>}
                </Fieldset>

                {shouldShowOrderComments &&
                    <OrderComments />}

                <div className="form-actions">
                    <Button
                        disabled={isUpdating || isResettingAddress}
                        id="checkout-billing-continue"
                        isLoading={isUpdating || isResettingAddress}
                        type="submit"
                        variant={ButtonVariant.Primary}
                    >
                        <TranslatedString id="common.continue_action" />
                    </Button>
                </div>
            </Form>
        )
    }

    private handleSelectAddress: (address: Partial<Address>) => void = async address => {
        const {
            updateAddress,
            onUnhandledError,
            setValues,
            isB3User,
        } = this.props

        this.setState({ isResettingAddress: true })
        try {
            if (B3Checkout.extrasFiles.getB3BillingAddressExtras()?.length) {
                let isEditAddress = B3Checkout.extrasFiles.setAddressExtrasFiles(isB3User, address, false)
                let getB2bCustomFields = isB3User ? B3Checkout.extrasFiles.setAddressSelectExtrasFiles(address) : {}
                const addressBilling: any = { ...this.props.values }
                Object.keys(addressBilling).map((item: any) => {
                    addressBilling[item] = (address as any)[item] || ''
                })
                if (!isEditAddress) await updateAddress(address)
                setValues({
                    ...addressBilling,
                    addressId: '',
                    b2bCustomFields: getB2bCustomFields
                } as BillingFormValues)
                return
            }
            await updateAddress(address)
            if (!this.validateAddress()) setValues({
                ...this.props.values,
                addressId: '',
            } as BillingFormValues)
        } catch (e) {
            onUnhandledError(e)
        } finally {
            this.setState({ isResettingAddress: false })
        }
    };

    private handleUseNewAddress: () => void = async () => {
        const {
            isB3User,
            b3Company,
            updateAddress,
            setValues,
        } = this.props

        if (isB3User) B3Checkout.extrasFiles.setDefaultAddress('billing')

        await updateAddress(isB3User ? b3Company.basicAddressInfo : {})

        if (!this.validateAddress()) setValues({
            ...this.props.values,
            addressId: '',
        } as BillingFormValues)

    };
}

const mapStateToProps = (state: { global: { isB3User: boolean, checkoutSettings: any }, company: any }) => {
    const {
        global: {
            isB3User,
            checkoutSettings,
        },
        company: b3Company
    } = state

    return {
        b3Company,
        isB3User,
        checkoutSettings,
    }
}

export default connect(mapStateToProps)(withLanguage(withFormik<B3Props & BillingFormProps & WithLanguageProps, BillingFormValues>({
    handleSubmit: (values, { props: { onSubmit } }) => {
        onSubmit(values)
    },
    mapPropsToValues: ({ getFields, customerMessage, billingAddress }) => ({
        ...mapAddressToFormValues(
            getFields(billingAddress && billingAddress.countryCode),
            billingAddress,
        ),
        b2bCustomFields: B3Checkout.extrasFiles.setGoBackEcho('billing'),
        orderComment: customerMessage,
    }),
    isInitialValid: ({
        billingAddress,
        getFields,
        language,
    }) => (
        !!billingAddress && getAddressFormFieldsValidationSchema({
            language,
            formFields: getFields(billingAddress.countryCode),
        }).isValidSync(billingAddress)
    ),
    validationSchema: ({
        language,
        getFields,
        methodId,
    }: BillingFormProps & WithLanguageProps) => methodId === 'amazonpay' ?
            (lazy<Partial<AddressFormValues>>(values => getCustomFormFieldsValidationSchema({
                translate: getTranslateAddressError(language),
                formFields: getFields(values && values.countryCode),
            }))) :
            (lazy<Partial<AddressFormValues>>(values => {
                return getAddressFormFieldsValidationSchema({
                    language,
                    formFields: getFields(values && values.countryCode),
                }).concat(object({
                    b2bCustomFields: B3Checkout.extrasFiles.getB3FormFieldsValidationSchema({
                        translate: B3Checkout.extrasFiles.getTranslateAddressError(language),
                        formFields: B3Checkout.extrasFiles.getB3ShippingAddressExtras(),
                    })
                }))
            })),
    enableReinitialize: true,
})(BillingForm)))
